import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropertyCard from '../auzland/PropertyCard';
import PropertyModal from '../auzland/PropertyModal';
import './RecentlySold.css';
import '../auzland/PropertyCard.css';
import './PropertiesPage.css';
import soldImg1 from '../media/properties/resold-img-1.jpg';
import soldImg2 from '../media/properties/resold-img-2.jpg';
import soldImg3 from '../media/properties/resold-img-3.jpg';

// API and Media URLs
const LISTINGS_API_URL = 'https://868qsxaw23.execute-api.us-east-2.amazonaws.com/Prod/listings';
const CLOUDFRONT_BASE_URL = 'https://dx9e0rbpjsaqb.cloudfront.net/';
const MEDIA_API_URL = 'https://868qsxaw23.execute-api.us-east-2.amazonaws.com/Prod/media';

// --- Dummy Data for fallback ---
const dummyData = [
  {
    id: 'dummy-1',
    address: '123 Sold Street',
    suburb: 'Exampleville',
    status: 'Sold',
    price: '$950,000',
    landSize: 450,
    propertyType: 'House',
    priceCustomerVisibility: '1',
    images: [soldImg1],
  },
  {
    id: 'dummy-2',
    address: '456 Another Place',
    suburb: 'Demo Park',
    status: 'Sold',
    price: '$1,200,000',
    landSize: 500,
    propertyType: 'Double Story',
    priceCustomerVisibility: '1',
    images: [soldImg2],
  },
  {
    id: 'dummy-3',
    address: '789 Test Avenue',
    suburb: 'Placeholder',
    status: 'Sold',
    price: '$800,000',
    landSize: 375,
    propertyType: 'Townhouse',
    priceCustomerVisibility: '1',
    images: [soldImg3],
  },
];

const RecentlySold = ({ setSectionRef }) => {
  const navigate = useNavigate();
  const [soldProperties, setSoldProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- Data Parsing Functions ---

  const parseCsv = (csv) => {
    const rows = [];
    if (!csv) return rows;

    const lines = [];
    let currentLine = '';
    let inQuotes = false;
    let i = 0;

    while (i < csv.length) {
      const char = csv[i];
      const nextChar = csv[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentLine += '"';
          i += 2;
        } else {
          inQuotes = !inQuotes;
          currentLine += char;
          i++;
        }
      } else if (char === '\n' && !inQuotes) {
        lines.push(currentLine);
        currentLine = '';
        i++;
      } else if (char === '\r' && nextChar === '\n' && !inQuotes) {
        lines.push(currentLine);
        currentLine = '';
        i += 2;
      } else {
        currentLine += char;
        i++;
      }
    }
    if (currentLine.trim()) {
      lines.push(currentLine);
    }

    if (lines.length < 2) return rows;

    const parseCsvLine = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;
      let i = 0;
      while (i < line.length) {
        const char = line[i];
        const nextChar = line[i + 1];
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i += 2;
          } else {
            inQuotes = !inQuotes;
            i++;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current);
          current = '';
          i++;
        } else {
          current += char;
          i++;
        }
      }
      values.push(current);
      return values.map(v => v.replace(/""/g, '"'));
    };

    const headers = parseCsvLine(lines[0]);

    for (let i = 1; i < lines.length; i++) {
      const values = parseCsvLine(lines[i]);
      if (values.length === headers.length) {
        const obj = {};
        headers.forEach((header, index) => {
          let value = values[index] || '';
          if (value.toLowerCase() === 'home and land packages') {
            value = 'Home & Land';
          }
          obj[header] = value;
        });
        rows.push(obj);
      }
    }
    return rows;
  };

  const toNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : undefined;
  };

  const buildCloudFrontUrl = (mediaKey) => {
    const key = mediaKey.startsWith('/') ? mediaKey.slice(1) : mediaKey;
    return `${CLOUDFRONT_BASE_URL}${key}`;
  };

  const getAllImagesFromMedia = useCallback(async (mediaString) => {
    if (!mediaString) return [];
    try {
      let mediaKeys = [];
      let cleanedString = mediaString.toString();
      if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
        cleanedString = cleanedString.slice(1, -1);
      }
      cleanedString = cleanedString.replace(/""/g, '"');

      if (cleanedString.startsWith('[') && cleanedString.endsWith(']')) {
        const content = cleanedString.slice(1, -1);
        if (content.trim()) {
          mediaKeys = content.split(',').map(key => key.trim());
        }
      } else {
        mediaKeys = JSON.parse(cleanedString);
      }

      if (!Array.isArray(mediaKeys) || mediaKeys.length === 0) return [];

      const mediaKeysFiltered = mediaKeys.filter(key => {
        if (typeof key !== 'string') return false;
        const extension = key.toLowerCase().split('.').pop();
        const allowedFormats = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg', 'pdf'];
        return allowedFormats.includes(extension);
      });

      if (mediaKeysFiltered.length === 0) return [];

      // Sort: Images first, PDFs last
      const sortedKeys = [...mediaKeysFiltered].sort((a, b) => {
        const isPdfA = a.toLowerCase().split('?')[0].endsWith('.pdf');
        const isPdfB = b.toLowerCase().split('?')[0].endsWith('.pdf');
        if (isPdfA && !isPdfB) return 1;
        if (!isPdfA && isPdfB) return -1;
        return 0;
      });

      return sortedKeys.map((imageKey) => buildCloudFrontUrl(imageKey));
    } catch (error) {
      console.error('❌ Error processing media:', error);
      // Fallback parsing
      try {
        if (typeof mediaString === 'string' && mediaString.includes('media/') && !mediaString.includes('[')) {
          const cleanPath = mediaString.replace(/['"]/g, '').trim();
          const extension = cleanPath.toLowerCase().split('.').pop();
          if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg', 'pdf'].includes(extension)) {
            return [buildCloudFrontUrl(cleanPath)];
          }
        }
        const mediaPathRegex = /media\/.*?\.(jpg|jpeg|png|gif|webp|bmp|svg|pdf)(?=[\s"'\]},]|$)/gi;
        const matches = mediaString.match(mediaPathRegex);
        if (matches && matches.length > 0) {
          const processedMatches = matches.map((imagePath) => imagePath.trim());

          // Sort fallback matches: Images first, PDFs last
          const sortedMatches = processedMatches.sort((a, b) => {
            const isPdfA = a.toLowerCase().split('?')[0].endsWith('.pdf');
            const isPdfB = b.toLowerCase().split('?')[0].endsWith('.pdf');
            if (isPdfA && !isPdfB) return 1;
            if (!isPdfA && isPdfB) return -1;
            return 0;
          });

          return sortedMatches.map((path) => buildCloudFrontUrl(path));
        }
      } catch (altError) {
        console.error('❌ Alternative parsing also failed:', altError);
      }
      return [];
    }
  }, []);

  // --- Helper to shuffle array ---
  const shuffleArray = (arr) => {
    return arr
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  };

  // --- Data Loading Effect ---

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        let res = await fetch(LISTINGS_API_URL);
        if (!res.ok) throw new Error(`Failed to load listings: HTTP ${res.status}`);
        const data = await res.json();
        const csv = typeof data === 'string' ? data : (data.csv || '');
        const rows = parseCsv(csv);

        const mapped = rows.map((r, idx) => ({
          id: r.id || `${r.address || 'property'}-${idx}`,
          address: r.address || '',
          suburb: r.suburb || '',
          propertyType: r.propertyType || r.property_type || '',
          lot: r.lot || '',
          availability: r.availability || '',
          status: r.status || 'For Sale',
          registrationConstructionStatus: r.registrationConstructionStatus || r.registration || r.constructionStatus || '',
          frontage: toNumber(r.frontage),
          price: r.price || r.price_guide || 'Price on request',
          landSize: toNumber(r.landSize) || toNumber(r.land_area_sqm),
          bedrooms: toNumber(r.bed),
          bathrooms: toNumber(r.bath),
          parking: toNumber(r.garage),
          media: r.media || r.media_url || '',
          description: r.description || r.Description || r.DESCRIPTION || '',
          images: [],
          priceNumber: (() => { const raw = (r.price || r.price_guide || '').toString(); const n = Number(raw.replace(/[^0-9]/g, '')); return Number.isFinite(n) ? n : undefined; })(),
          propertyCustomerVisibility: r.propertyCustomerVisibility || '1',
          priceCustomerVisibility: r.priceCustomerVisibility || '0'
        })).filter((p) => (p.address || p.suburb));

        // Filter for SOLD properties
        const sold = mapped.filter(p => p.availability?.toLowerCase().trim() === 'sold');

        if (sold.length > 0) {
          // 1. Shuffle the Sold properties
          const shuffledSold = shuffleArray(sold);

          // 2. Take only the top 3 random properties
          const selectedSold = shuffledSold.slice(0, 3);

          // 3. Set state immediately with these 3
          setSoldProperties(selectedSold);

          // 4. Load images ONLY for these 3 selected properties
          const loadImages = async () => {
            const results = [...selectedSold];

            await Promise.all(
              results.map(async (property, index) => {
                try {
                  const imageUrls = await getAllImagesFromMedia(property.media);
                  results[index] = { ...property, images: imageUrls };
                } catch (_) {
                  results[index] = { ...property, images: [] };
                }
              })
            );

            setSoldProperties(results);
          };

          loadImages();

        } else {
          // No real sold properties found, use dummy data
          setSoldProperties(dummyData);
        }

      } catch (e) {
        console.error("Error loading sold properties:", e);
        setSoldProperties([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [getAllImagesFromMedia]); // Only run once on mount

  // --- Modal Handlers ---

  const handlePropertyClick = async (property) => {
    setSelectedProperty(property);
    setIsModalOpen(true);

    // Attempt to load images if they haven't loaded yet
    if (!property?.images || property.images.length === 0) {
      try {
        const imageUrls = await getAllImagesFromMedia(property.media);
        setSoldProperties(prev => prev.map(p => p.id === property.id ? { ...p, images: imageUrls } : p));
        setSelectedProperty(prev => prev && prev.id === property.id ? { ...prev, images: imageUrls } : prev);
      } catch (e) {
        console.error("Error loading images on click:", e);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
  };

  const handleViewMoreClick = () => {
    navigate('/buy', { state: { filter: { availability: 'sold' } } });
  };

  // We already sliced to 3 in the useEffect, but this keeps it safe
  const propertiesToShow = useMemo(() => soldProperties.slice(0, 3), [soldProperties]);

  // --- Render Logic ---

  const renderContent = () => {
    if (isLoading) {
      return <div className="sold-properties-message">Loading sold properties...</div>;
    }

    if (propertiesToShow.length > 0) {
      return (
        <div className="recently-sold-grid">
          {propertiesToShow.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              onViewDetails={() => handlePropertyClick(property)}
            />
          ))}
        </div>
      );
    }

    return <div className="sold-properties-message">No sold properties to display right now.</div>;
  };

  return (
    <section
      id="recently-sold"
      ref={setSectionRef('recently-sold')}
      className="section recently-sold-section animate-in"
    >
      <div className="container">
        <div className="section-header">
          <h2 className="section-title handwriting-font">
            Recently Sold
          </h2>
          <p className="section-subtitle">
            See what properties have just been snapped up in your area.
          </p>
        </div>

        {renderContent()}

        <div className="view-more-container">
          <button className="btn btn-secondary btn-large" onClick={handleViewMoreClick}>
            View More Sold
          </button>
        </div>
      </div>

      <PropertyModal
        property={selectedProperty}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </section>
  );
};

export default RecentlySold;
